import {gsap} from "gsap/all";
import $ from "jquery";
import Swiper from "swiper";
import {Navigation} from "swiper/modules";
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/effect-creative';
import 'swiper/css/effect-fade';

export class RoverUI {
  constructor(app) {
    this.app = app;

    // Game data
    this.content = this.app?.data?.content || {};
    this.gameData = this.content.game || {};

    // Get default color
    const defaultColorObj = (this.gameData.colors || []).find(c => c.default === true);
    this.defaultColorId = defaultColorObj ? String(defaultColorObj.id) : "6";

    this.$screen = $("#screen-3");
    this.$roverContainer = this.$screen.find(".rover-container");
    this.$swiperContainer = this.$screen.find("#rover-selector-swiper");
    this.$colorPickerContainer = this.$screen.find("#colors-picker");

    this.swiper = null;
    this.currentOptionType = null;

    // init
    this.init();
  }

  init() {
    const swiperEl = this.$swiperContainer.find('.swiper')[0];
    if (!swiperEl) return;

    this.swiper = new Swiper(swiperEl, {
      modules: [Navigation],
      slidesPerView: 1,
      speed: 400,
      loop: true,
      threshold: 20,
      centeredSlides: true,
      navigation: {
        nextEl: this.$swiperContainer.find('.rover-button-next')[0],
        prevEl: this.$swiperContainer.find('.rover-button-prev')[0],
      },
      on: {
        slidePrevTransitionStart: () => {
          this.onSelectionChange("left");
        },
        slideNextTransitionStart: () => {
          this.onSelectionChange("right");
        },
      }
    });
  }

  /**
   * Calculate the required width/height top/left to wrap the element we're modifying on the rover
   */
  updateSlideDimensions() {
    if (!this.currentOptionType) return;
    const hasCockpit = this.app.game.rovers[0]?.config.cockpit.id !== "";
    if (this.currentOptionType !== "cockpit" && !hasCockpit) return;

    // Get elements that are target and used to calculate dimensions and position
    const $activeCockpit = this.$roverContainer.find(".rover-preview > .cockpit-view.is-active");
    let $targetElements;
    if (this.currentOptionType === "cockpit") {
      $targetElements = this.$screen.find(".rover-preview > .cockpit-view");
    } else {
      const $categoryGroup = $activeCockpit.find(
        `.option-group[data-category="${this.currentOptionType}"]`
      );
      $targetElements = $categoryGroup.find(".option-slide .option-image");
      if (!$targetElements.length) {
        $targetElements = $categoryGroup.find(".option-image");
      }
    }

    const roverContainerBounding = this.$roverContainer[0].getBoundingClientRect();
    const containerWidth = roverContainerBounding.width;
    const containerHeight = roverContainerBounding.height;

    const outerGap = 46; // minimum padding between slider and box
    let finalLeft = outerGap;
    let finalTop = outerGap;
    let finalWidth = `calc(100% - ${outerGap * 2}px)`;
    let finalHeight = `calc(100% - ${outerGap * 2}px)`;

    if ((this.currentOptionType === "cockpit" || this.currentOptionType === "wheels") && $targetElements.length) {
      let minLeft = Infinity;
      let minTop = Infinity;
      let maxRight = -Infinity;
      let maxBottom = -Infinity;

      $targetElements.each((_, el) => {
        const rect = el.getBoundingClientRect();

        const left = rect.left - roverContainerBounding.left;
        const top = rect.top - roverContainerBounding.top;
        const right = rect.right - roverContainerBounding.left;
        const bottom = rect.bottom - roverContainerBounding.top;

        minLeft = Math.min(minLeft, left);
        minTop = Math.min(minTop, top);
        maxRight = Math.max(maxRight, right);
        maxBottom = Math.max(maxBottom, bottom);
      });

      if (minLeft !== Infinity) {
        const innerGapX = this.currentOptionType === "cockpit" ? 50 : 100;
        const innerGapTop = 30; // minimum padding inside the slider
        const innerGapBottom = this.currentOptionType === "cockpit" ? 74 : 30;

        minLeft -= innerGapX;
        maxRight += innerGapX;
        minTop -= innerGapTop;
        maxBottom += innerGapBottom;

        let width = maxRight - minLeft;
        let height = maxBottom - minTop;

        const maxWidth = containerWidth - outerGap * 2;
        const maxHeight = containerHeight - outerGap * 2;

        width = Math.min(width, maxWidth);
        height = Math.min(height, maxHeight);

        let left = Math.max(outerGap, minLeft);
        let top = Math.max(outerGap, minTop);

        left = Math.min(left, containerWidth - outerGap - width);
        top = Math.min(top, containerHeight - outerGap - height);

        finalLeft = left;
        finalTop = top;
        finalWidth = `${width}px`;
        finalHeight = `${height}px`;
      }
    }

    // Update slider to new width/height and position
    gsap.to(this.$swiperContainer, {
      duration: 0.3,
      ease: "power1.out",
      left: finalLeft,
      top: finalTop,
      width: finalWidth,
      height: finalHeight,
      overwrite: true
    });

    // Change "z-index" of swiper when selecting wheels
    this.$swiperContainer.toggleClass("is-wheels-active", this.currentOptionType === "wheels");
  }

  /**
   * Used to detect and update the visual of the rover using the slider
   * @param direction
   */
  onSelectionChange(direction) {
    if (!this.currentOptionType) return;

    if (Array.isArray(this.app.game.rovers)) {
      // Get old stats
      const currentStats = this.app.game.rovers[0].getConfigStats();

      // Update rover and synchronize the finale rover
      this.app.game.rovers[0].updateVisual(this.currentOptionType, null, direction);
      this.app.game.rovers[1].applyConfig(this.app.game.rovers[0].getConfigCode())

      // Update stats
      const newStats = this.app.game.rovers[0].getConfigStats();
      this.app.game.updateStats(newStats, currentStats);

      // Update label/description using rover config
      const optionDetails = this.getOptionDetails();
      if (optionDetails) this.app.game.updateOptionDescription(optionDetails);
    }

    // Enable/Disable button based on selections
    this.handleButtons();

    // Update final screen
    this.app.game.updateQRCode();
    this.app.game.updateStrengthAndTasks();
  }

  /**
   * A category was selected (or deselected if it was already the active one)
   * @param optionType
   */
  setOptionType(optionType) {

    // Option already active, deselect
    if (this.currentOptionType === optionType) {
      this.currentOptionType = null;

      // Hide selection slider
      this.$swiperContainer.removeClass("is-active is-wheels-active");

      // Deactivate category buttons
      this.$screen.find(".rover-choice-btn .pill-button").removeClass("is-active");

      // Deactivate stats status
      $(".property-list .property-item").removeClass("up-value middle-value bottom-value warning-value");

      // Disable color picker
      //this.setColorPickerState(false);

      // Trigger option description update for the newly selected category
      const optionDetails = this.getOptionDetails();
      if (optionDetails) this.app.game.updateOptionDescription(optionDetails);

      return;
    }

    // Set new option type
    this.currentOptionType = optionType;

    // Hide tutorial popup
    if (optionType === "cockpit") {
      // Kill step timeline if needed
      if (this.app.currentTimeline) {
        this.app.currentTimeline.clear();
        this.app.currentTimeline.kill();
      }
      gsap.killTweensOf($(".step"));
      gsap.to($(".step"), {autoAlpha: 0.0, duration: 0.3, ease: "power1.out"});
      $(".step").removeClass("current");
    }

    // Show selection slider
    this.$swiperContainer.addClass("is-active");

    // Get active color for option type
    let activeColorId = this.app.game.rovers?.[0]?.config?.[optionType]?.color;
    if (!activeColorId) activeColorId = this.defaultColorId;

    // Update the color picker state (enable/disable). Only cockpit and wheels have color.
    this.setColorPickerState(["cockpit", "wheels"].includes(optionType));

    this.$screen.find(".rover-choice-btn .pill-button").removeClass("is-active");
    const $currentButton = this.$screen.find(`.rover-choice-btn .pill-button[data-action-arg="${optionType}"]`);
    $currentButton.addClass("is-active");

    // Update class
    this.$colorPickerContainer.find(".pastille").removeClass("is-selected");
    this.$colorPickerContainer.find(`.pastille[data-color-id="${activeColorId}"]`).addClass("is-selected");

    // Trigger option description update for the newly selected category
    const optionDetails = this.getOptionDetails();
    if (optionDetails) this.app.game.updateOptionDescription(optionDetails);

    // Resize slider to fit the newly selected option
    // Finish any slide transition still running, so no part stays half visible and transforms don't skew the measure
    const $slides = this.$roverContainer.find('.option-slide');
    gsap.set($slides.filter('.is-active'), {
      autoAlpha: 1,
      x: 0,
      overwrite: true
    });
    gsap.set($slides.not('.is-active'), {
      autoAlpha: 0,
      x: 0,
      overwrite: true
    });
    this.updateSlideDimensions();

    // Automatically select the first set of wheels when selecting the category
    if (optionType === "wheels" && this.app.game.rovers[0]?.config.wheels.id === "") {
      this.onSelectionChange("right");
    } else {
      // Refresh stats and restore up/down/middle indicators
      const currentStats = this.app.game.rovers[0].getConfigStats();
      this.app.game.updateStats(currentStats, currentStats);
    }
  }

  /**
   * Set a color value to the current selected option type.
   */
  setColor(colorId) {
    // An active category is needed to be able to change color
    if (!this.currentOptionType) return;

    if (Array.isArray(this.app.game.rovers)) {
      this.app.game.rovers.forEach(rover => {
        rover.updateColor(this.currentOptionType, colorId);
      });
    }

    // Update class
    this.$colorPickerContainer.find(".pastille").removeClass("is-selected");
    this.$colorPickerContainer.find(`.pastille[data-color-id="${colorId}"]`).addClass("is-selected");

    // Update QR src in final screen
    this.app.game.updateQRCode();
  }

  /**
   * Enable or disable the color picker
   */
  setColorPickerState(targetState) {
    if (targetState) {
      // Enable color picker
      gsap.set(this.$colorPickerContainer, {pointerEvents: "auto"})
      gsap.to(this.$colorPickerContainer, {autoAlpha: 1, duration: 0.3, ease: "power1.out"});
    } else {
      // Disable color picker
      gsap.set(this.$colorPickerContainer, {pointerEvents: "none"})
      gsap.to(this.$colorPickerContainer, {autoAlpha: 0.1, duration: 0.3, ease: "power1.out"});
      this.$colorPickerContainer.find(".pastille").removeClass("is-selected");
    }
  }

  /**
   * Retrieves the localized label and description for the currently active option type.
   * @returns {Object|null} An object containing localized label and text fields, or null if no valid rover/option is selected.
   */
  getOptionDetails() {

    let details = {
      label: {fr: "", en: "No accessory"},
      description: {fr: "", en: ""},
      pros: {fr: "", en: ""},
      cons: {fr: "", en: ""}
    };

    if (this.currentOptionType && Array.isArray(this.app.game.rovers)) {

      // Get cockpit data for active cockpit
      const activeCockpitId = this.app.game.rovers[0].config.cockpit?.id;
      const cockpitData = this.app.game.rovers[0].gameData.rover?.[activeCockpitId];

      // If we have a cockpit selected, us cockpit data or current selection
      if (activeCockpitId && cockpitData) {
        const optionId = this.app.game.rovers[0].config[this.currentOptionType]?.id;
        const selection = (this.currentOptionType === "cockpit") ? cockpitData : cockpitData[this.currentOptionType]?.[optionId];
        details = {...details, ...selection};
      }
    }

    return details;
  }

  /**
   * Enable/Disable button based on selections
   */
  handleButtons() {
    const hasCockpit = this.app.game.rovers[0]?.config.cockpit.id !== "";
    const hasWheels = this.app.game.rovers[0]?.config.wheels.id !== "";
    const $rightPanel = this.$screen.find(".right-panel");
    const $otherChoices = $rightPanel.find(".rover-choice-btn > div:not([data-action-arg='cockpit'])");

    // Enable/Disable the other option if there's a cockpit
    $otherChoices.toggleClass("not-active", !hasCockpit);
    gsap.set($otherChoices, {clearProps: "opacity"});

    // Enable/Disable finish vehicle button
    // When we have at least a cockpit with wheels
    $rightPanel.find(".finishVehicle").toggleClass("is-ready", hasCockpit && hasWheels);
  }

  reset(partial) {
    this.currentOptionType = null;

    // Reset buttons
    this.$screen.find(".right-panel .rover-choice-btn > div, .right-panel .finishVehicle").addClass("no-transition");
    this.$screen.find(".right-panel .rover-choice-btn > div").removeClass("is-active not-active");
    this.$screen.find(".right-panel .finishVehicle").removeClass("is-ready");

    // When partial reset, ex. user go back to screen 3 from 4
    if (partial) {
      this.$screen.find(".right-panel .rover-choice-btn > div:not([data-action-arg='cockpit'])").addClass("not-active");
    }

    // Reset color to default
    this.$colorPickerContainer.find(".pastille").removeClass("is-selected");
    this.$colorPickerContainer.find(".pastille.is-default").addClass("is-selected");

    // Reset swiper
    this.$swiperContainer.addClass("no-transition");
    this.$swiperContainer.removeClass("is-active is-wheels-active");

    // Double requestAnimationFrame waits for the DOM to paint the reset state before re-enabling transitions
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.$screen.find(".right-panel .rover-choice-btn > div, .right-panel .finishVehicle").removeClass("no-transition");
        this.$swiperContainer.removeClass("no-transition");
      });
    });
  }
}
